import { HttpClient, HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@page2flip/core';
import { Configuration, Publication } from '@page2flip/core/common';
import { Observable } from 'rxjs';
import { throwError } from 'rxjs/internal/observable/throwError';
import { scan } from 'rxjs/operators';
import { TemplateElement } from '../templates/templates.component';

/**
 * Service to perform the CSV upload & publication generation.
 */
@Injectable()
export class UploadService {

  /** WkService API URL. */
  private readonly generatorApiUrl = 'https://wkservice.space.one/api'; // TODO: change to final URL

  /** space.one Bumblebee converter API URL. */
  private readonly converterApiUrl = 'https://bumblebee.space.one/api'; // TODO: change to final URL

  /** space.one Bumblebee preview API URL. */
  private readonly previewApiUrl = 'https://bumblebee.space.one/preview'; // TODO: change to final URL

  /** Whether or not there was an API error. */
  apiError: boolean;

  /** Catalog language. */
  language: string;

  /** Catalog titles. */
  private readonly titles = {
    cs: 'Prodejní Katalog 2021 | 1. Pololetí',
    de: 'Verkaufskatalog 2021 | 1. Halbjahr',
    en: 'Sales Catalogue 2021 | 1nd Half-Year',
    es: 'Catálogo Comercial 2021 | 1º Semestre',
    fr: 'Catalogue De Vente 2021 | Semestre 1',
    it: 'Catalogo Di Vendita 2021 | 1° Semestre',
    sv: 'Säljkatalog 2021 | 1:A Halvåret',
    zh: '销售目录 2021 | 上半年 '
  };

  /** Catalog file names. */
  private readonly files = {
    cs: 'LW-Verkaufskatalog_2021-1Hj_cze.pdf',
    de: 'LW-Verkaufskatalog_2021-1Hj_deu.pdf',
    en: 'LW-Verkaufskatalog_2021-1Hj_eng.pdf',
    es: 'LW-Verkaufskatalog_2021-1Hj_esp.pdf',
    fr: 'LW-Verkaufskatalog_2021-1Hj_fra.pdf',
    it: 'LW-Verkaufskatalog_2021-1Hj_ita.pdf',
    sv: 'LW-Verkaufskatalog_2021-1Hj_swe.pdf',
    zh: 'LW-Verkaufskatalog_2021-1Hj_chin.pdf'
  };

  private readonly priceTypeLabels = {
    cs: 'Ceny',
    de: 'Preisstellung',
    en: 'Price type',
    es: 'Tipo de precio',
    fr: 'Fixation des prix',
    it: 'Determinazione del prezzo',
    sv: 'Prissättning',
    zh: '定价'
  };

  private readonly priceDateLabels = {
    cs: 'Stav cen',
    de: 'Preisstand',
    en: 'Price date',
    es: 'Fecha de los precios',
    fr: 'Tarif en vigueur',
    it: 'Stato dei prezzi',
    sv: 'Aktuella priser',
    zh: '价格状态'
  };

  private readonly marketLabels = {
    cs: 'Trh',
    de: 'Markt',
    en: 'Market',
    es: 'Mercado',
    fr: 'Marché',
    it: 'Mercato',
    sv: 'Marknad',
    zh: '市场'
  };

  /**
   * Constructor of the service.
   *
   * @param window  DI Token for the Window object.
   * @param http    Service to perform HTTP requests.
   */
  constructor(@Inject(WINDOW) private window: any,
              private http: HttpClient) {
    http
      .get(this.generatorApiUrl)
      .subscribe(
        () => {
          //
        },
        (error: any) => {
          this.apiError = true;
          return throwError(new Error(error));
        });
    http
      .get(this.converterApiUrl)
      .subscribe(
        () => {
          //
        },
        (error: any) => {
          this.apiError = true;
          return throwError(new Error(error));
        });
  }

  /**
   * Generates the PDF.
   *
   * @param data    Meta data.
   * @param csvFile The CSV file.
   */
  generatePdf(data: any, csvFile: File): Observable<HttpResponse<any>> {
    this.language = data.language;

    const formData: FormData = new FormData();
    formData.append('priceType', this.priceTypeLabels[data.language] + ': ' + data.priceType);
    formData.append('priceDate', this.priceDateLabels[data.language] + ': ' + data.priceDate);
    formData.append('market', this.marketLabels[data.language] + ': ' + data.market);
    formData.append('language', data.language);
    formData.append("currency", data.currency);
    formData.append('csv', csvFile);

    return this.http.post(this.generatorApiUrl + '/generate', formData, { observe: 'response' });
  }

  /**
   * Replace the PDF.
   *
   * @param data    Meta data.
   * @param pdfFile The PDF file.
   */
  replacePdf(data: any, pdfFile: File): Observable<Upload> {
    const formData: FormData = new FormData();
    formData.append('lang', data.language);
    formData.append('pdf', pdfFile);
    const initialState: Upload = { state: 'PENDING', progress: 0 }
    const calculateState = (upload: Upload, event: HttpEvent<unknown>): Upload => {
      if (isHttpProgressEvent(event)) {
        return {
          progress: event.total
            ? Math.round((100 * event.loaded) / event.total)
            : upload.progress,
          state: 'IN_PROGRESS',
        }
      }
      if (isHttpResponse(event)) {
        return {
          progress: 100,
          state: 'DONE',
        }
      }
      return upload
    }
    return this.http.post(this.generatorApiUrl + '/templates', formData, { observe: 'events', reportProgress: true })
      .pipe(scan(calculateState, initialState))
  }

  /**
   * Creates the medium.
   *
   * @param url PDF location.
   */
  createMedium(url: string): Observable<HttpResponse<any>> {
    return this.http.post(this.converterApiUrl + '/media/fromUrl?url=' + url, null, { observe: 'response' });
  }

  createCheckUrl(mediaId: String, submediaId: String) {
    return this.converterApiUrl + '/medium/' + mediaId + '/submedium/' + submediaId;
  }

  createPreviewUrl(mediaId: String, submediaId: String) {
    return this.previewApiUrl+'/preview/'+mediaId+'/submedia/'+submediaId;
  }

  /**
   * Creates the sub-medium.
   *
   * @param url Medium location.
   */
  createSubMedium(url: string): Observable<HttpResponse<any>> {
    const configuration: Configuration = <Configuration>{
      design: {
        colors: {
          primaryBackground: '#ffffff',
          primaryForeground: '#4c5356',
          secondaryBackground: '#ebebeb',
          secondaryForeground: '#5c9db0'
        },
        animateHotspots: false,
        background: false,
        backgroundSize: 'unset',
        transparency: true
      },
      features: {
        bookmarks: true,
        download: true,
        feedback: false,
        help: true,
        info: false,
        notes: false,
        pageComparison: false,
        pageOverview: true,
        search: true,
        share: false,
        shoppingCart: true,
        tableOfContents: false,
        watchList: false
      },
      layout: {
        animateCorners: true,
        cornerSize: 100,
        documentMargin: 75,
        doublePage: true,
        hideToolbar: false,
        menuPosition: 'left',
        pageFlipAnimation: true,
        pageFlipSound: true,
        pageMarkerWidth: 100,
        sidePanelPosition: 'right',
        toolbarPosition: 'top'
      },
      legal: {
        imprint: '© Volkswagen Aktiengesellschaft\n\nKonzern After Sales  \nK-AVO-RW Werkstattausrüstung\n\nBrieffach 011/4915  \n38436 Wolfsburg  \nDeutschland',
        termsOfService: null,
        privacyPolicy: null
      },
      options: {
        customCSS: ".printable-shopping-cart {\n  display: none;\n}\n\n@media print {\n  * {\n    overflow: visible !important;\n  }\n\n  p2f-side-panel > * {\n    display: block !important;\n  }\n\n  p2f-side-panel {\n    box-shadow: none !important;\n  }\n\n  p2f-shopping-cart .side-panel-title,\n  p2f-shopping-cart .side-panel-content .item-wrapper,\n  p2f-shopping-cart .side-panel-content .info,\n  p2f-shopping-cart .side-panel-footer {\n    display: none;\n  }\n\n  .printable-shopping-cart {\n    display: block;\n  }\n\n  .printable-shopping-cart input {\n    border-top: 1px solid #000;\n    display: block;\n    margin-top: 50px;\n    padding-top: 10px;\n    width: 300px;\n  }\n\n  .printable-shopping-cart input::placeholder {\n    color: #000;\n  }\n\n  .printable-shopping-cart h1 {\n    margin: 50px 0 20px 0 !important;\n  }\n\n  .printable-shopping-cart table {\n    border-collapse: collapse;\n    width: 100%;\n  }\n\n  .printable-shopping-cart table,\n  .printable-shopping-cart th,\n  .printable-shopping-cart td {\n    border: 1px solid #000;\n  }\n\n  .printable-shopping-cart table .footer td {\n    border: none;\n  }\n\n  .printable-shopping-cart th {\n    text-align: left;\n  }\n\n  .printable-shopping-cart th,\n  .printable-shopping-cart td {\n    padding: 2px 4px;\n  }\n\n  .printable-shopping-cart table .qty,\n  .printable-shopping-cart table .price,\n  .printable-shopping-cart table .total {\n    text-align: right;\n  }\n\n  .printable-shopping-cart table .footer {\n    font-weight: bold;\n  }\n}",
        customJS: "document.addEventListener('featureLoaded', function (event) {\n    var publicationId = window['data'].publication.id;\n\n    var language = document.getElementsByTagName('html')[0].getAttribute('lang');\n    var locales = ['cs', 'de', 'es', 'fr', 'it', 'sv', 'zh'];\n    var locale = locales.indexOf(language) > -1 ? language : 'en';\n\n    var messages = {\n        cs: {\n            articleDescription: 'Označení',\n            articleNumber: 'Čís.položky',\n            articleQuantity: 'Číslo',\n            articleTotalPrice: 'Celková cena',\n            articleUnitPrice: 'Cena jednotlivě',\n            currency: 'EUR',\n            formImporter: 'Dovozce',\n            formContactPartner: 'Kontaktní osoba',\n            formTelefax: 'Fax',\n            formWorkshopNumber: 'Provozní číslo',\n            formName: 'Jméno objednavatele tiskacími písmeny',\n            formSignature: 'Datum / podpis / razítko společnosti',\n            tableOrderFrom: 'Objednávka od ',\n            tableTotal: 'Celkem bez DPH',\n            priceOnRequest: 'Cena na vyžádání'\n        },\n        de: {\n            articleDescription: 'Bezeichnung',\n            articleNumber: 'Artikelnummer',\n            articleQuantity: 'Anzahl',\n            articleTotalPrice: 'Gesamtpreis',\n            articleUnitPrice: 'Enzeilpreis',\n            currency: 'EUR',\n            formImporter: 'Importeur',\n            formContactPartner: 'Ansprechpartner',\n            formTelefax: 'Telefax',\n            formWorkshopNumber: 'Betriebsnummer',\n            formName: 'Name des Bestellers in Druckbuchstaben',\n            formSignature: 'Datum / Unterschrift / Firmenstempel',\n            tableOrderFrom: 'Bestellung vom ',\n            tableTotal: 'Summe exkl. MwSt.',\n            priceOnRequest: 'Preis auf Anfrage'\n        },\n        en: {\n            articleDescription: 'Description',\n            articleNumber: 'Article number',\n            articleQuantity: 'Quantity',\n            articleTotalPrice: 'Total price',\n            articleUnitPrice: 'Unit price',\n            currency: 'EUR',\n            formImporter: 'Importer',\n            formContactPartner: 'Contact partner',\n            formTelefax: 'Telefax',\n            formWorkshopNumber: 'Workshop number',\n            formName: 'Name of the orderer in block letters',\n            formSignature: 'Date / Signature / Company stamp',\n            tableOrderFrom: 'Order from ',\n            tableTotal: 'Total, excl. VAT',\n            priceOnRequest: 'Price on request'\n        },\n        es: {\n            articleDescription: 'Etiqueta',\n            articleNumber: 'Número de artículo',\n            articleQuantity: 'Cantidad',\n            articleTotalPrice: 'Importe total',\n            articleUnitPrice: 'Precio unitario',\n            currency: 'EUR',\n            formImporter: 'Importador',\n            formContactPartner: 'Persona de contacto',\n            formTelefax: 'Fax',\n            formWorkshopNumber: 'Número de compañía',\n            formName: 'Nombre del comprador en mayúsculas',\n            formSignature: 'Fecha / firma / sello de la empresa',\n            tableOrderFrom: 'Pedido de ',\n            tableTotal: 'Total sin IVA',\n            priceOnRequest: 'Precio a petición'\n        },\n        fr: {\n            articleDescription: 'Désignation',\n            articleNumber: 'Numéro d’article',\n            articleQuantity: 'Nombre',\n            articleTotalPrice: 'Prix total',\n            articleUnitPrice: 'Prix à l’unité',\n            currency: 'EUR',\n            formImporter: 'Importateur',\n            formContactPartner: 'Interlocuteur',\n            formTelefax: 'Fax',\n            formWorkshopNumber: 'Numéro d\\'identification de l’entreprise',\n            formName: 'Nom du client en caractères d’imprimerie',\n            formSignature: 'Date / Signature / Tampon de l\\'entreprise',\n            tableOrderFrom: 'Commande du ',\n            tableTotal: 'Total hors TVA',\n            priceOnRequest: 'Prix sur demande'\n        },\n        it: {\n            articleDescription: 'Elemento identificatore',\n            articleNumber: 'Codice articolo',\n            articleQuantity: 'Numero',\n            articleTotalPrice: 'Prezzo totale',\n            articleUnitPrice: 'Prezzo unitario',\n            currency: 'EUR',\n            formImporter: 'Importatore',\n            formContactPartner: 'Referente',\n            formTelefax: 'Fax',\n            formWorkshopNumber: 'Codice azienda',\n            formName: 'Nome del cliente in stampatello',\n            formSignature: 'Data / Firma / Timbro aziendale',\n            tableOrderFrom: 'Ordine da ',\n            tableTotal: 'Totale IVA esclusa',\n            priceOnRequest: 'Prezzo su richiesta'\n        },\n        sv: {\n            articleDescription: 'Beteckning',\n            articleNumber: 'Artikelnummer',\n            articleQuantity: 'Antal',\n            articleTotalPrice: 'Totalpris',\n            articleUnitPrice: 'Enhetspris',\n            currency: 'EUR',\n            formImporter: 'Importör',\n            formContactPartner: 'Kontaktperson',\n            formTelefax: 'Fax',\n            formWorkshopNumber: 'Företagsnummer',\n            formName: 'Beställarens namn i tryckbokstäver',\n            formSignature: 'Datum / signatur / företagsstämpel',\n            tableOrderFrom: 'Beställning från ',\n            tableTotal: 'Summa exklusive moms',\n            priceOnRequest: 'Pris på begäran'\n        },\n        zh: {\n            articleDescription: '标签',\n            articleNumber: '商品编号',\n            articleQuantity: '数量',\n            articleTotalPrice: '总价',\n            articleUnitPrice: '单价',\n            currency: 'EUR',\n            formImporter: '进口商',\n            formContactPartner: '联系人',\n            formTelefax: '传真',\n            formWorkshopNumber: '企业编号',\n            formName: '印刷字体表示的客户名称',\n            formSignature: '日期 / 签名 / 公司印章',\n            tableOrderFrom: '订单来自 ',\n            tableTotal: '不包括增值税的总额',\n            priceOnRequest: '应要求提供的价格'\n        }\n    };\n\n    function getMessage(id) {\n        return messages[locale][id];\n    }\n\n    function formatPrice(price) {\n        return price.toLocaleString(locale, {style: 'currency', currency: getMessage('currency')});\n    }\n\n    if (event.detail === 'hotspot' || event.detail === 'shoppingCart') {\n        var priceInterval = setInterval(function () {\n            var prices = document.querySelectorAll('span.price:not(.total)');\n\n            for (var i = 0; i < prices.length; i++) {\n                var price = prices[i];\n\n                if (parseFloat(price.innerHTML) === 0) {\n                    price.innerHTML = getMessage('priceOnRequest');\n                }\n            }\n\n            if (!prices.length) {\n                clearInterval(priceInterval);\n            }\n        }, 500);\n    }\n\n    if (event.detail === 'shoppingCart') {\n        var shoppingCartInterval = setInterval(function () {\n            var publicationData = JSON.parse(localStorage.getItem(publicationId));\n            var shoppingCart = publicationData ? publicationData['shoppingCart'] : [];\n\n            var sum = 0;\n\n            var tableHtml = '<table><tr>';\n            tableHtml += '<th>' + getMessage('articleQuantity') + '</th>';\n            tableHtml += '<th>' + getMessage('articleNumber') + '</th>';\n            tableHtml += '<th>' + getMessage('articleDescription') + '</th>';\n            tableHtml += '<th>' + getMessage('articleUnitPrice') + '</th>';\n            tableHtml += '<th>' + getMessage('articleTotalPrice') + '</th>';\n            tableHtml += '</tr>';\n\n            for (var i = 0; i < shoppingCart.length; i++) {\n                var article = shoppingCart[i];\n\n                var quantity = '<td class=\"qty\">' + article.quantity + '</td>';\n                var articleId = '<td class=\"id\">' + article.id + '</td>';\n                var title = '<td class=\"title\">' + article.title + '<br><i>' + (article.additionalInfo ? article.additionalInfo : '') + '</i></td>';\n\n                var price = article.price ?\n                    '<td class=\"price\">' + formatPrice(article.price) + '</td>' :\n                    '<td class=\"price\">' + getMessage('priceOnRequest') + '</td>';\n\n                var total = article.price ?\n                    '<td class=\"total\">' + formatPrice(article.quantity * article.price) + '</td>' :\n                    '<td class=\"total\">&mdash;</td>';\n\n                tableHtml += '<tr>' + quantity + articleId + title + price + total + '</tr>';\n\n                if (article.price && article.quantity) {\n                    sum += article.quantity * article.price;\n                }\n            }\n\n            tableHtml += '<tr class=\"footer\"><td>&nbsp;</td><td>&nbsp;</td><td>' + getMessage('tableTotal') + '</td><td>&nbsp;</td><td class=\"total\">' + formatPrice(sum) + '</td></table>';\n\n            var date = new Date();\n            var day = date.getDate() < 10 ? '0' + date.getDate() : date.getDate();\n            var month = (date.getMonth() + 1) < 10 ? '0' + (date.getMonth() + 1) : (date.getMonth() + 1);\n            var dateString = day + '.' + month + '.' + date.getFullYear();\n\n            var printDiv = document.createElement('div');\n            printDiv.classList.add('printable-shopping-cart');\n            printDiv.innerHTML = '<input placeholder=\"' + getMessage('formImporter') + '\">';\n            printDiv.innerHTML += '<input placeholder=\"' + getMessage('formContactPartner') + '\">';\n            printDiv.innerHTML += '<input placeholder=\"' + getMessage('formTelefax') + '\">';\n            printDiv.innerHTML += '<h1>' + getMessage('tableOrderFrom') + dateString + '</h1>';\n            printDiv.innerHTML += tableHtml;\n            printDiv.innerHTML += '<input placeholder=\"' + getMessage('formWorkshopNumber') + '\">';\n            printDiv.innerHTML += '<input placeholder=\"' + getMessage('formName') + '\">';\n            printDiv.innerHTML += '<input placeholder=\"' + getMessage('formSignature') + '\">';\n\n            var shoppingCartElement = document.querySelector('p2f-shopping-cart > .side-panel-content');\n\n            if (shoppingCartElement) {\n                // remove old print div\n                var oldPrintDiv = document.querySelector('.printable-shopping-cart');\n                oldPrintDiv && oldPrintDiv.parentNode.removeChild(oldPrintDiv);\n                // append new print div\n                shoppingCartElement.appendChild(printDiv);\n                shoppingCartElement.insertBefore(printDiv, shoppingCartElement.firstChild);\n            } else {\n                clearInterval(shoppingCartInterval);\n            }\n        }, 500);\n    }\n\n    if (event.detail === 'legal') {\n        document.getElementById('legal-imprint').remove();\n    }\n});\n",
        indexing: false,
        loader: true,
        tracking: false
      },
      shop: {
        additionalInfo: true,
        articleRegExp: '(ASE)(\\d{3})?(\\d{3})?(\\d{2})?([A-Z0-9]{3})?',
        currency: '€',
        endpoint: null,
        vat: 0,
        vatIncluded: false
      }
    };

    const publication: Publication = <Publication>{
      meta: {
        author: 'Volkswagen Aktiengesellschaft',
        description: '',
        keywords: '',
        language: this.language,
        title: this.titles[this.language]
      },
      pdf: {
        file: this.files[this.language]
      },
      release: {
        online: true,
        type: 'stable'
      }
    };

    const transformerOptions: string = JSON.stringify({
      transformer: 'page2flip',
      transformerParameter: { configuration, publication }
    });

    return this.http.post(url + '/submedia', transformerOptions, {
      headers: { 'Content-Type': 'application/json' },
      observe: 'response'
    });
  }

  /**
   * Checks the converter status.
   *
   * @param url Sub-medium location.
   */
  checkStatus(url: string): Observable<HttpResponse<any>> {
    return this.http.get(url, { observe: 'response' });
  }

  fetchTemplates(): Observable<HttpResponse<any>> {
    return this.http.get(this.generatorApiUrl+"/templates", {observe: 'response'});
  }
}

function isHttpResponse<T>(event: HttpEvent<T>): event is HttpResponse<T> {
  return event.type === HttpEventType.Response
}

function isHttpProgressEvent(
  event: HttpEvent<unknown>
): event is HttpProgressEvent {
  return (
    event.type === HttpEventType.DownloadProgress ||
    event.type === HttpEventType.UploadProgress
  )
}

export interface Upload {
  progress: number
  state: 'PENDING' | 'IN_PROGRESS' | 'DONE'
}
